import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleUp = keyframes`
  from {
    transform: scale(0.96) translateY(6px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  background-color: transparent;
`;

export const Content = styled.div<{ $isTable?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;

  ${({ $isTable }) =>
    $isTable
      ? `
    overflow: hidden;
    padding: 0;
    margin-top: 15px;
    font-family: 'Inter', sans-serif;

    > * {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      width: 100%;
    }

    table {
      width: 100%;
    }
  `
      : `
    overflow-y: auto;
    padding: 20px 0 40px 0;
    gap: 18px;

    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
  `}
`;





export const FilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  width: 100%;
`;

export const FileCard = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-3px);
    border-color: #cbd5e1;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  }
`;

export const CardTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
`;

export const TypeBadge = styled.span<{ $type: string }>`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 7px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ $type }) => {
    if ($type === 'image') return '#e0f2fe';
    if ($type === 'pdf') return '#fee2e2';
    if ($type === 'sheet') return '#dcfce7';
    if ($type === 'doc') return '#e0e7ff';
    return '#f1f5f9';
  }};
  color: ${({ $type }) => {
    if ($type === 'image') return '#0284c7';
    if ($type === 'pdf') return '#b91c1c';
    if ($type === 'sheet') return '#15803d';
    if ($type === 'doc') return '#4338ca';
    return '#475569';
  }};
`;

export const CardMenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #64748b;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

export const CardMenuDropdown = styled.div`
  position: absolute;
  top: 36px;
  right: 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  z-index: 50;
  min-width: 170px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: ${fadeIn} 0.12s ease-out;
`;

export const CardMenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  color: ${({ $danger }) => ($danger ? '#dc2626' : '#334155')};
  cursor: pointer;
  text-align: left;
  transition: all 0.1s ease;

  &:hover {
    background: ${({ $danger }) => ($danger ? '#fee2e2' : '#f1f5f9')};
    color: ${({ $danger }) => ($danger ? '#b91c1c' : '#0f172a')};
  }
`;

export const ThumbnailWrapper = styled.div`
  width: 100%;
  height: 140px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #f8fafc;
    padding: 4px;
    transition: transform 0.25s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }
`;

export const ZoomBadge = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(15, 23, 42, 0.75);
  color: #ffffff;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateY(4px);
  transition: all 0.18s ease;
  pointer-events: none;

  ${ThumbnailWrapper}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const FileIconPlaceholder = styled.div<{ $type: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${({ $type }) => {
    if ($type === 'pdf') return '#dc2626';
    if ($type === 'sheet') return '#16a34a';
    if ($type === 'doc') return '#2563eb';
    return '#64748b';
  }};

  span {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

export const CardBody = styled.div`
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

export const FileName = styled.h4`
  margin: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

export const FileMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 2px;
`;

export const CardFooter = styled.div`
  padding: 8px 12px;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const UsageBadgeButton = styled.button<{ $hasUsage?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ $hasUsage }) => ($hasUsage ? '#0090D3' : '#94a3b8')};
  padding: 2px 4px;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    color: #0077b0;
    text-decoration: underline;
  }
`;

export const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-bottom: 24px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  text-align: center;
  color: #64748b;

  svg {
    color: #94a3b8;
  }

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: #1e293b;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #64748b;
    max-width: 380px;
  }
`;

export const EmptyActionBtn = styled.button`
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #0090D3;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #007bb4;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 16px;
  animation: ${fadeIn} 0.18s ease-out;
`;

export const ModalContainer = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 540px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: ${scaleUp} 0.18s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
`;

export const ModalTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

export const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 70vh;
  overflow-y: auto;
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
`;

export const DropzoneArea = styled.div<{ $isDragOver: boolean }>`
  border: 2px dashed ${({ $isDragOver }) => ($isDragOver ? '#0090D3' : '#cbd5e1')};
  background: ${({ $isDragOver }) => ($isDragOver ? '#f0f9ff' : '#f8fafc')};
  border-radius: 12px;
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #0090D3;
    background: #f0f9ff;
  }
`;

export const DropzoneIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0090D3;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const InputLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
`;

export const StyledInput = styled.input.attrs({
  className: 'inline-input',
})`
  height: 40px !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  padding: 0 12px !important;
  font-size: 0.85rem !important;
  color: #0f172a !important;
  outline: none !important;
  transition: all 0.15s ease;

  &:hover {
    border-color: #94a3b8 !important;
  }

  &:focus {
    border-color: #0090D3 !important;
    box-shadow: 0 0 0 3px rgba(0, 144, 211, 0.15) !important;
    background: #ffffff !important;
  }
`;

export const CancelButton = styled.button`
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

export const PrimaryButton = styled.button<{ $danger?: boolean }>`
  background: ${({ $danger }) => ($danger ? '#dc2626' : '#0090D3')};
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${({ $danger }) => ($danger ? '#b91c1c' : '#007bb4')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const UsageItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.15s ease;

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
  }
`;

export const UsageItemLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow: hidden;
`;

export const UsageBadge = styled.span`
  background: #e2e8f0;
  color: #334155;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  align-self: flex-start;
`;

export const UsageRecordLink = styled.a`
  font-size: 0.82rem;
  font-weight: 600;
  color: #0090D3;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    text-decoration: underline;
  }
`;

export const UsageEditButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }
`;
